import { createContext, useContext, useMemo, useCallback } from "react";
import type { ReactNode } from "react"
import { useQuery, useMutation, useConvexAuth } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useAuth } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export type UsageFeature =
  | "aiStudySets"
  | "aiChatMessages"
  | "quizzes"
  | "documentSummaries"
  | "youtubeToNotes"
  | "pdfToNotes"
  | "aiAutocomplete";

export interface UsageLimit {
  hasReachedLimit: boolean;
  usage: number;
  limit: number;
  remaining: number;
}

export interface UsageData {
  aiStudySets: number;
  aiChatMessages: number;
  quizzes: number;
  documentSummaries: number;
  youtubeToNotes: number;
  pdfToNotes: number;
  aiAutocomplete: number;
  autocompleteDate?: string;
}

// Feature limits based on pricing.md
const FREE_PLAN_LIMITS: Record<UsageFeature, number> = {
  aiStudySets: 5,
  aiChatMessages: 0,
  quizzes: 0,
  documentSummaries: 0,
  youtubeToNotes: 0,
  pdfToNotes: 0,
  aiAutocomplete: 0,
};

const PLUS_PLAN_LIMITS: Record<UsageFeature, number> = {
  aiStudySets: 15,
  aiChatMessages: 30,
  quizzes: 15,
  documentSummaries: 15,
  youtubeToNotes: 15,
  pdfToNotes: 15,
  aiAutocomplete: 50,
};

const SCHOLAR_PLAN_LIMITS: Record<UsageFeature, number> = {
  aiStudySets: 100,
  aiChatMessages: 300,
  quizzes: 100,
  documentSummaries: 100,
  youtubeToNotes: 100,
  pdfToNotes: 100,
  aiAutocomplete: Infinity, // Unlimited
};

// Helper to get current date in user's local timezone
function getLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Helper to get current month-year in user's local timezone
function getLocalMonthYear(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

interface UsageContextType {
  usage: UsageData | null | undefined;
  limits: Record<UsageFeature, number>;
  hasFreePlan: boolean;
  hasPlusPlan: boolean;
  hasScholarPlan: boolean;
  getUsageForFeature: (feature: UsageFeature) => UsageLimit;
  canUseFeature: (feature: UsageFeature) => boolean;
  trackUsage: (feature: UsageFeature, amount?: number) => Promise<void>;
  getUpgradeMessage: (feature: UsageFeature) => string;
  getProcessingLimitMessage: (type: "pdf" | "youtube", limit: string) => string;
  checkFeatureAccessAndRedirect: (feature: UsageFeature) => boolean;
}

export const UsageContext = createContext<UsageContextType | undefined>(undefined);

export const UsageProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useConvexAuth();

  // Memoize stable date values per render cycle / mounting session
  const localDate = useMemo(() => getLocalDate(), []);
  const localMonthYear = useMemo(() => getLocalMonthYear(), []);

  // Subscribe to usage query exactly once for authenticated users
  const usage = useQuery(
    api.usageTracking.getUserUsage,
    isAuthenticated
      ? { clientDate: localDate, clientMonthYear: localMonthYear }
      : "skip"
  ) as UsageData | null | undefined;

  const incrementUsage = useMutation(api.usageTracking.incrementUsage);
  const { has } = useAuth();
  const hasPlusPlan = has?.({ plan: "plus_plan" }) ?? false;
  const hasScholarPlan = has?.({ plan: "scholar_plan" }) ?? false;
  const hasFreePlan = !hasPlusPlan && !hasScholarPlan;
  const navigate = useNavigate();

  const limits = useMemo(() => {
    if (hasScholarPlan) return SCHOLAR_PLAN_LIMITS;
    if (hasPlusPlan) return PLUS_PLAN_LIMITS;
    return FREE_PLAN_LIMITS;
  }, [hasPlusPlan, hasScholarPlan]);

  const getUsageForFeature = (feature: UsageFeature): UsageLimit => {
    const currentUsage = usage?.[feature] || 0;
    const limit = limits[feature];

    return {
      hasReachedLimit: currentUsage >= limit,
      usage: currentUsage,
      limit,
      remaining: Math.max(0, limit - currentUsage),
    };
  };

  const canUseFeature = (feature: UsageFeature): boolean => {
    const usageData = getUsageForFeature(feature);
    return !usageData.hasReachedLimit;
  };

  const trackUsage = async (feature: UsageFeature, amount: number = 1) => {
    if (!canUseFeature(feature)) {
      throw new Error(`Usage limit reached for ${feature}`);
    }

    await incrementUsage({
      feature,
      amount,
      clientDate: localDate,
      clientMonthYear: localMonthYear,
    });
  };

  const getUpgradeMessage = (feature: UsageFeature): string => {
    if (hasScholarPlan) {
      const scholarFeatureMessages: Record<UsageFeature, string> = {
        aiStudySets: "You've reached your monthly limit of 100 AI study sets. Your limit will reset next month.",
        aiChatMessages: "You've reached your monthly limit of 300 AI Chat messages. Your limit will reset next month.",
        quizzes: "You've reached your monthly limit of 100 AI quizzes. Your limit will reset next month.",
        documentSummaries: "You've reached your monthly limit of 100 document summaries. Your limit will reset next month.",
        youtubeToNotes: "You've reached your monthly limit of 100 YouTube-to-notes conversions. Your limit will reset next month.",
        pdfToNotes: "You've reached your monthly limit of 100 PDF-to-notes conversions. Your limit will reset next month.",
        aiAutocomplete: "You've reached your monthly limit of AI autocomplete suggestions. Your limit will reset next month.",
      };
      return scholarFeatureMessages[feature];
    }

    if (hasPlusPlan) {
      const plusFeatureMessages: Record<UsageFeature, string> = {
        aiStudySets: "You've reached your monthly limit of 15 AI study sets. Upgrade to Scholar for 100/month!",
        aiChatMessages: "You've reached your monthly limit of 30 AI Chat messages. Upgrade to Scholar for 300/month!",
        quizzes: "You've reached your monthly limit of 15 AI quizzes. Upgrade to Scholar for 100/month!",
        documentSummaries: "You've reached your monthly limit of 15 document summaries. Upgrade to Scholar for 100/month!",
        youtubeToNotes: "You've reached your monthly limit of 15 YouTube-to-notes conversions. Upgrade to Scholar for 100/month!",
        pdfToNotes: "You've reached your monthly limit of 15 PDF-to-notes conversions. Upgrade to Scholar for 100/month!",
        aiAutocomplete: "You've reached your monthly limit of 50 autocomplete suggestions. Upgrade to Scholar for unlimited autocomplete!",
      };
      return plusFeatureMessages[feature];
    }

    const freeFeatureMessages: Record<UsageFeature, string> = {
      aiStudySets: "You've reached your monthly limit of 5 AI study sets. Upgrade to Plus or Scholar for more!",
      aiChatMessages: "AI Chat is not available on the Free plan. Upgrade to Plus or Scholar to use AI Chat!",
      quizzes: "AI quizzes are not available on the Free plan. Upgrade to Plus or Scholar to use AI quizzes!",
      documentSummaries: "AI document summaries are not available on the Free plan. Upgrade to Plus or Scholar to use summaries!",
      youtubeToNotes: "YouTube to notes is not available on the Free plan. Upgrade to Plus or Scholar to use conversions!",
      pdfToNotes: "PDF to notes is not available on the Free plan. Upgrade to Plus or Scholar to use conversions!",
      aiAutocomplete: "AI Autocomplete is not available on the Free plan. Upgrade to Plus or Scholar for autocomplete!",
    };

    return freeFeatureMessages[feature];
  };

  const getProcessingLimitMessage = (type: "pdf" | "youtube", limit: string): string => {
    if (type === "pdf") {
      if (hasScholarPlan) {
        return `This PDF ${limit}. Scholar plan supports up to 100MB and unlimited pages.`;
      } else if (hasPlusPlan) {
        return `This PDF ${limit}. Upgrade to Scholar to process files up to 100MB!`;
      } else {
        return `This PDF ${limit}. Upgrade to Plus (25MB) or Scholar (100MB) for larger files!`;
      }
    } else {
      if (hasScholarPlan) {
        return `This video is too long. Scholar plan supports videos up to 45 minutes.`;
      } else if (hasPlusPlan) {
        return `This video is too long (over 15 minutes). Upgrade to Scholar to process videos up to 45 minutes!`;
      } else {
        return `Free plan does not support YouTube video imports. Upgrade to Plus (15 min) or Scholar (45 min)!`;
      }
    }
  };

  const checkFeatureAccessAndRedirect = useCallback(
    (feature: UsageFeature): boolean => {
      const usageData = getUsageForFeature(feature);
      if (usageData.hasReachedLimit) {
        const isFree = !hasPlusPlan && !hasScholarPlan;
        if (isFree) {
          toast.error("Upgrade to Plus or Scholar to access this feature!");
          navigate("/pricing");
        } else {
          toast.error(getUpgradeMessage(feature));
        }
        return false;
      }
      return true;
    },
    [getUsageForFeature, hasPlusPlan, hasScholarPlan, navigate, getUpgradeMessage]
  );

  const contextValue = useMemo(
    () => ({
      usage,
      limits,
      hasFreePlan,
      hasPlusPlan,
      hasScholarPlan,
      getUsageForFeature,
      canUseFeature,
      trackUsage,
      getUpgradeMessage,
      getProcessingLimitMessage,
      checkFeatureAccessAndRedirect,
    }),
    [usage, limits, hasFreePlan, hasPlusPlan, hasScholarPlan, checkFeatureAccessAndRedirect]
  );

  return <UsageContext.Provider value={contextValue}>{children}</UsageContext.Provider>;
};

export const useUsageContext = () => {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error("useUsageContext must be used within a UsageProvider");
  }
  return context;
};
